import React from "react";
import { navigate } from "react-big-calendar/lib/utils/constants";
import { isAtForwardLimit, MAX_DAYS_AHEAD } from "./calendarLimits";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft } from "@fortawesome/free-solid-svg-icons/faChevronLeft";
import { faChevronRight } from "@fortawesome/free-solid-svg-icons/faChevronRight";

export const CalendarToolbar: React.FC<Props> = ({
  localizer: { messages },
  label,
  onNavigate,
  onView,
  view,
  views,
  date,
  onToolbarState,
}) => {
  // Publishes what this toolbar is showing so the page header can mirror it once the
  // toolbar itself has scrolled out of view. react-big-calendar owns the label's
  // formatting, so reporting it is more faithful than re-deriving the date upstream.
  // Plain data only — a callback here would get a new identity every render and drive
  // the effect in a loop.
  React.useEffect(() => {
    onToolbarState?.({ view, label, date });
  }, [onToolbarState, view, label, date]);

  // Wide enough to hit with a thumb without growing the toolbar: the height the
  // buttons add is taken back from the view-switcher's bottom margin above them.
  const arrowButton: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 64,
    minHeight: 40,
    margin: "2px 0",
    padding: "0 16px",
    // A filled slate pill, so the control reads as tappable against the calendar's
    // black rather than as a loose glyph.
    background: "rgba(148, 163, 184, 0.14)",
    border: "none",
    borderRadius: 8,
    color: "#e2e8f0",
    cursor: "pointer",
  };

  // Computed here from the toolbar's own `date` rather than passed down: the Toolbar
  // component is memoised upstream, and a new prop would remount it on every change.
  const atForwardLimit = isAtForwardLimit(date);

  return (
    // Slate band lifting the whole toolbar off the black calendar below it, matching
    // the sticky header on the Parts tab.
    <div className="bg-gray-900 border-b border-gray-600">
      <div className="flex flex-row justify-between items-center px-2">
        <button type="button" style={arrowButton} aria-label="Previous" onClick={() => onNavigate(navigate.PREVIOUS)}>
          {/* @ts-ignore */}
          <FontAwesomeIcon icon={faChevronLeft} style={{ fontSize: 22 }} />
        </button>
        <div style={{ fontWeight: 600, fontSize: 15, color: "#f1f5f9" }}>{label}</div>
        <button
          type="button"
          style={{ ...arrowButton, opacity: atForwardLimit ? 0.35 : 1, cursor: atForwardLimit ? "default" : "pointer" }}
          aria-label="Next"
          disabled={atForwardLimit}
          title={atForwardLimit ? `The schedule is only shown ${MAX_DAYS_AHEAD} days ahead` : undefined}
          onClick={() => onNavigate(navigate.NEXT)}
        >
          {/* @ts-ignore */}
          <FontAwesomeIcon icon={faChevronRight} style={{ fontSize: 22 }} />
        </button>
      </div>
    </div>
  );
};

type Props = {
  view: string;
  views: string[];
  label: React.ReactNode;
  localizer: any;
  onNavigate: any;
  onView: any;
  date?: Date;
  onToolbarState?: (state: { view: string; label: React.ReactNode; date?: Date }) => void;
};
